// Helpers for the Investigation page's AI SIG check: turn the schedule the AI
// returned into the row shape the Changing FQY panel saves, and decide whether
// it is safe to save without the pharmacist typing it in.

const DOSE_COUNT_BY_FREQUENCY = { OD: 1, BID: 2, TID: 3, QID: 4, '5x': 5, '6x': 6, '7x': 7 };

// Doses are decimals (4.3 ML x 3 days), so totals are compared with a small
// tolerance instead of ===, which fails on ordinary floating point rounding.
const QTY_TOLERANCE = 0.001;

const canonicalFrequency = (value) => {
    const raw = String(value ?? 'OD').trim();
    const upper = raw.toUpperCase();
    if (DOSE_COUNT_BY_FREQUENCY[upper]) return upper;
    const lower = raw.toLowerCase();
    return DOSE_COUNT_BY_FREQUENCY[lower] ? lower : null;
};

const blockDoses = (block) => {
    if (Array.isArray(block?.dose_rows) && block.dose_rows.length > 0) {
        return block.dose_rows.map(doseRow => doseRow?.dose);
    }
    const doses = block?.dose ?? block?.doses;
    if (doses === null || doses === undefined || doses === '') return [];
    return Array.isArray(doses) ? doses : [doses];
};

/**
 * Returns the AI schedule as save-ready rows, or null when any cycle cannot be
 * read safely (missing dose, missing days, dose count not matching frequency).
 * The AI is asked for dose: ["5"], but it can answer with a bare "5" or 5.
 */
export const normalizeAiChangingSchedule = (schedule) => {
    const blocks = schedule?.frequency;
    if (!Array.isArray(blocks) || blocks.length === 0) return null;

    const rows = [];

    for (const block of blocks) {
        const frequency = canonicalFrequency(block?.frequency);
        const days = parseInt(block?.days, 10);
        const doses = blockDoses(block).map(dose => parseFloat(dose));

        if (!frequency || !(days >= 1)) return null;
        if (doses.length !== DOSE_COUNT_BY_FREQUENCY[frequency]) return null;
        if (doses.some(dose => !Number.isFinite(dose) || dose < 0)) return null;

        rows.push({
            frequency,
            dose: doses.map(String),
            dose_rows: doses.map(dose => ({ dose: String(dose), iv_code: '' })),
            days,
            ...(block?.cycle_no != null && { cycle_no: block.cycle_no })
        });
    }

    return rows;
};

export const aiScheduleTotalQty = (rows) => (rows || []).reduce(
    (total, row) => total + row.dose_rows.reduce((sum, doseRow) => sum + parseFloat(doseRow.dose), 0) * row.days,
    0
);

export const aiScheduleTotalDays = (rows) => (rows || []).reduce((total, row) => total + row.days, 0);

/**
 * The AI schedule is only saved on its own when it accounts for the whole
 * prescription: fentanyl must cover the day supply, other non-Dilaudid drugs
 * must add up to the authorized quantity. Anything else is left for the
 * pharmacist to enter.
 */
export const shouldAutoSaveAiChangingSchedule = ({ rows, isNonDilaudid, isFentanyl, authorizedQty, daySupply }) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;

    if (isFentanyl && aiScheduleTotalDays(rows) === Number(daySupply)) return true;

    const targetQty = parseFloat(authorizedQty);
    return Boolean(isNonDilaudid)
        && Number.isFinite(targetQty)
        && Math.abs(aiScheduleTotalQty(rows) - targetQty) < QTY_TOLERANCE;
};

/**
 * True when dose rows are actually saved for the Rx. get_requency_data is
 * never empty on its own any more (the server always adds delivery_gap), so
 * "is the object empty" no longer answers this.
 */
export const hasSavedFrequencyRows = (frequencyData) =>
    Array.isArray(frequencyData?.frequency) && frequencyData.frequency.length > 0;

/**
 * True when the only saved rows are a flat "same dose every day" row the
 * system made by itself and no pharmacist has touched. Mirrors
 * areOnlySystemDefaultRows() in ProcessInvestigationAiSigCheck on the backend.
 */
export const isSystemDefaultFrequencyData = (frequencyData) => {
    if (!hasSavedFrequencyRows(frequencyData)) return false;
    if (frequencyData.is_changing || frequencyData.update_type !== 'system') return false;

    return frequencyData.frequency.every(group =>
        !group?.iv_code_updated_by
        && !group?.dose_code_updated_by
        && (group?.dose_rows || []).every(doseRow => !doseRow?.dose_code_updated_by)
    );
};

/**
 * True when the AI is allowed to put this Rx on Changing: no pharmacist has
 * entered doses (nothing saved, or only the system's flat daily row) and it
 * is not single dispense. Safe-supply system rows come from the prescribed
 * med times, so they are left alone.
 */
export const canAiSetChangingDose = (patientDetails) => {
    if (Number(patientDetails?.is_single_dispense) === 1) return false;

    const frequencyData = patientDetails?.get_requency_data;
    if (!hasSavedFrequencyRows(frequencyData)) return true;

    const isSafeSupply = String(patientDetails?.rxsafesupply ?? '').trim().toUpperCase() === 'Y';
    return !isSafeSupply && isSystemDefaultFrequencyData(frequencyData);
};

/**
 * The AI has read the SIG as a changing dose and the changing schedule is
 * still owed. The page then shows Changing instead of a flat daily dose.
 */
export const needsAiChangingDoseSetup = (patientDetails) => {
    const flag = patientDetails?.is_ai_changing_dose;
    return (flag === true || Number(flag) === 1) && canAiSetChangingDose(patientDetails);
};

/**
 * True when the AI's tablet split may be saved as the Rx's doses. The split is
 * for a SIG with a tablet range ("1-3 TABLETS"); the AI sometimes splits a
 * plain dose anyway ("TAKE 16 CAPSULES ... DAILY" came back as 3,3,3,3,3,1 and
 * was saved as 6x). A Daily Dispense Rx stays OD with one dose. Mirrors the
 * guard in ProcessInvestigationAiSigCheck::prefillAiFrequencyData on the backend.
 */
export const canSaveAiTabletDistribution = (patientDetails, sig) => {
    if (Number(patientDetails?.billing_frequency) === 1) return false;

    return /\b\d+(?:\.\d+)?\s*-\s*\d+(?:\.\d+)?\s*(?:TAB|TABS|TABLET|TABLETS|CAP|CAPS|CAPSULE|CAPSULES)\b/i
        .test(String(sig ?? ''));
};
